import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { X } from "lucide-react";

const ROLES = ["Tutti", "Portiere", "Difensore", "Centrocampista", "Attaccante"];
const ROLE_PLURALS = { Portiere: "Portieri", Difensore: "Difensori", Centrocampista: "Centrocampisti", Attaccante: "Attaccanti" };

export default function Squadre() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [squad, setSquad] = useState("FIGC");
  const [role, setRole] = useState("Tutti");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/players").then((r) => setPlayers(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => players.filter((p) => p.squad === squad && (role === "Tutti" || p.position === role)),
    [players, squad, role]
  );

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid="squadre-page">
      <p className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-gold mb-4">Giocatore per giocatore</p>
      <h1 className="font-display font-black uppercase text-6xl sm:text-8xl lg:text-9xl text-white leading-[0.85] tracking-tighter mb-12" data-testid="squadre-title">
        Le <span className="text-gold">Squadre</span>
      </h1>

      <div className="flex flex-wrap gap-3 mb-6" data-testid="squadre-squad-tabs">
        <button
          onClick={() => setSquad("FIGC")}
          data-testid="squad-tab-figc"
          className={`font-display uppercase tracking-wider font-bold text-xl px-6 py-3 border-2 transition-colors ${
            squad === "FIGC" ? "bg-gold text-night border-gold" : "border-line text-zinc-300 hover:border-gold hover:text-gold"
          }`}
        >
          Prima Squadra FIGC
        </button>
        <button
          onClick={() => setSquad("CSI")}
          data-testid="squad-tab-csi"
          className={`font-display uppercase tracking-wider font-bold text-xl px-6 py-3 border-2 transition-colors ${
            squad === "CSI" ? "bg-gold text-night border-gold" : "border-line text-zinc-300 hover:border-gold hover:text-gold"
          }`}
        >
          Seconda Squadra CSI
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-12" data-testid="squadre-role-filters">
        {ROLES.map((r) => (
          <button
            key={r}
            onClick={() => setRole(r)}
            data-testid={`filter-role-${r.toLowerCase()}`}
            className={`font-mono text-xs tracking-[0.15em] uppercase px-4 py-2 border transition-colors ${
              role === r ? "bg-white text-night border-white font-semibold" : "border-line text-zinc-400 hover:border-white hover:text-white"
            }`}
          >
            {r === "Tutti" ? "Tutti" : ROLE_PLURALS[r]}
          </button>
        ))}
      </div>

      {loading && <p className="text-zinc-400 font-mono text-sm" data-testid="squadre-loading">Caricamento rosa...</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {filtered.map((p) => (
          <button
            key={p.id}
            onClick={() => setSelected(p)}
            data-testid={`player-card-${p.id}`}
            className="group relative grey-fade border border-line p-6 text-left overflow-hidden hover:border-gold transition-colors"
          >
            <span className="absolute -top-4 -right-2 font-display font-black text-[7rem] leading-none text-stroke-gold opacity-30 group-hover:opacity-70 transition-opacity">
              {p.number}
            </span>
            <span className="inline-block bg-gold text-night font-mono text-[10px] font-semibold tracking-[0.2em] uppercase px-2 py-1 mb-4">
              {p.position}
            </span>
            {p.bio && (
              <span className={`inline-block font-mono text-[10px] font-semibold tracking-[0.2em] uppercase px-2 py-1 mb-4 ml-2 ${p.bio === "Capitano" ? "bg-white text-night" : "border border-white text-white"}`}>
                {p.bio}
              </span>
            )}
            <h3 className="font-display font-black uppercase text-3xl text-white leading-none">{p.name}</h3>
            <div className="mt-5 grid grid-cols-3 gap-2 border-t border-line pt-4">
              {(p.position === "Portiere"
                ? [[p.appearances, "Presenze"], [p.goals_conceded ?? 0, "Gol Subiti"], [p.clean_sheets ?? 0, "Clean Sheet"]]
                : [[p.appearances, "Presenze"], [p.goals, "Gol"], [p.assists, "Assist"]]
              ).map(([v, l], idx) => (
                <div key={l}>
                  <p className={`font-display font-black text-2xl leading-none ${idx === 0 ? "text-gold" : "text-white"}`}>{v}</p>
                  <p className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mt-1">{l}</p>
                </div>
              ))}
            </div>
          </button>
        ))}
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-[60] bg-night/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="player-modal-overlay"
        >
          <div
            className="grey-fade border border-gold max-w-lg w-full p-10 relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="player-modal"
          >
            <button
              onClick={() => setSelected(null)}
              className="absolute top-4 right-4 text-zinc-400 hover:text-gold"
              data-testid="player-modal-close"
              aria-label="Chiudi"
            >
              <X size={26} />
            </button>
            <span className="font-display font-black text-8xl text-stroke-gold leading-none">{selected.number}</span>
            <h3 className="font-display font-black uppercase text-4xl sm:text-5xl text-white mt-2 leading-none">{selected.name}</h3>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-gold mt-2">
              {selected.position} · {selected.squad === "FIGC" ? "Prima Squadra FIGC" : "Seconda Squadra CSI"}
              {selected.bio ? ` · ${selected.bio}` : ""}
            </p>
            {selected.bio && !["Capitano", "Vice Capitano"].includes(selected.bio) && (
              <p className="text-zinc-300 mt-5">{selected.bio}</p>
            )}
            <div className="mt-6 grid grid-cols-3 gap-3 border-t border-line pt-5">
              {(selected.position === "Portiere"
                ? [["Presenze", selected.appearances], ["Gol Subiti", selected.goals_conceded ?? 0], ["Clean Sheet", selected.clean_sheets ?? 0]]
                : [["Presenze", selected.appearances], ["Gol", selected.goals], ["Assist", selected.assists]]
              ).map(([l, v]) => (
                <div key={l} className="text-center">
                  <p className="font-display font-black text-4xl text-gold leading-none">{v}</p>
                  <p className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mt-1">{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
